/**
 * LegalReason Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { toast } from 'sonner';
import { useLegalReasonStore } from '@/store/legal-reason.store.js';
import { useTenantStore } from '@/store/tenant.store';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';

export class LegalReasonIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch legal reasons and update store
     */
    async fetchLegalReasons(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useLegalReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setReasons(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create legal reason
     */
    async createLegalReason(data) {
        const store = useLegalReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:legalBasisCreated', { defaultValue: 'Legal basis created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update legal reason
     */
    async updateLegalReason(id, data) {
        const store = useLegalReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('integrationToasts:legalBasisUpdated', { defaultValue: 'Legal basis updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete legal reason
     */
    async deleteLegalReason(id) {
        const store = useLegalReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:legalReasonDeleted', { defaultValue: 'Legal reason deleted' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete legal reasons
     */
    async bulkDeleteLegalReasons(ids) {
        const store = useLegalReasonStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);
            toast.success(i18next.t('integrationToasts:nLegalReasonsDeleted', {
                count: ids.length,
                defaultValue: `${ids.length} legal reasons deleted`,
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch dropdown parameters
     */
    async fetchDropdownData() {
        try {
            return await fetchDropdownOptionsFromReference('legalReason', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            });
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Legal reason not found',
        });
    }
}
